<template>
  <ModalWrapper
    ref="modal"
    title="Apply for Leave"
    size="large"
  >
    <template #body>
      <form @submit.prevent="submitLeaveRequest" class="leave-apply-form">
        <div class="hr-form-grid hr-mb-3">
          <div class="span-full">
            <label class="hr-form-label">Leave Type <span class="required">*</span></label>
            <select
              class="hr-select"
              v-model="formData.leaveTypeID"
              @change="onLeaveTypeChange"
              required
            >
              <option value="">Select Leave Type</option>
              <option v-for="type in leaveTypes" :key="type.id" :value="type.id">
                {{ type.typeName }} ({{ getAvailableDaysForType(type.id) }} days available)
              </option>
            </select>
            <small v-if="selectedLeaveType" class="hr-form-hint">Available Days: {{ getAvailableDays() }}</small>
          </div>
          <div class="span-full">
            <label class="hr-form-label">Duration Type <span class="required">*</span></label>
            <div class="hr-flex hr-gap-3">
              <label class="hr-radio-wrap">
                <input type="radio" value="full" v-model="durationType">
                <span>Full Day(s)</span>
              </label>
              <label class="hr-radio-wrap">
                <input type="radio" value="half" v-model="durationType">
                <span>Half Day</span>
              </label>
            </div>
          </div>
          <div>
            <label class="hr-form-label">Start Date <span class="required">*</span></label>
            <input
              type="date"
              class="hr-input"
              v-model="formData.startDate"
              :min="minDate"
              @change="calculateDays"
              required
            >
          </div>
          <div>
            <label class="hr-form-label">
              {{ durationType === 'half' ? 'Date' : 'End Date' }} <span class="required">*</span>
            </label>
            <input
              type="date"
              class="hr-input"
              v-model="formData.endDate"
              :min="formData.startDate || minDate"
              :disabled="durationType === 'half'"
              @change="calculateDays"
              required
            >
          </div>
        </div>

        <div v-if="durationType === 'half'" class="hr-mb-3">
          <label class="hr-form-label">Half Day Session</label>
          <div class="hr-flex hr-gap-3">
            <label class="hr-radio-wrap">
              <input type="radio" value="morning" v-model="halfDaySession">
              <span>Morning (First Half)</span>
            </label>
            <label class="hr-radio-wrap">
              <input type="radio" value="afternoon" v-model="halfDaySession">
              <span>Afternoon (Second Half)</span>
            </label>
          </div>
        </div>

        <div class="hr-info-block hr-mb-3">
          <div class="hr-info-block-body">
            <span><i class="bx bx-info-circle hr-me-2"></i></span>
            <strong>Total Days Requested:</strong> {{ calculatedDays }} day(s)
            <span v-if="durationType === 'full'" class="hr-form-hint d-block mt-1">Weekends and public holidays are not deducted.</span>
            <span v-if="calculatedDays > getAvailableDays()" class="text-exceed">(Exceeds available balance!)</span>
          </div>
        </div>

        <div class="hr-mb-3">
          <label class="hr-form-label">Reason for Leave <span class="required">*</span></label>
          <textarea
            class="hr-textarea"
            v-model="formData.reason"
            rows="4"
            placeholder="Please provide a brief explanation for your leave request..."
            required
            maxlength="500"
          ></textarea>
          <small class="hr-form-hint">{{ (formData.reason || '').length }}/500 characters</small>
        </div>

        <div class="hr-mb-3">
          <label class="hr-form-label">Supporting Documents (Optional)</label>
          <input
            type="file"
            class="hr-input"
            ref="fileInput"
            @change="handleFileUpload"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            multiple
          >
          <small class="hr-form-hint">Accepted formats: PDF, JPG, PNG, DOC, DOCX (Max 5MB each)</small>
        </div>

        <div class="hr-form-grid">
          <div>
            <label class="hr-form-label">Contact Number During Leave</label>
            <input
              type="tel"
              class="hr-input"
              v-model="formData.contactNumber"
              placeholder="Enter phone number"
            >
          </div>
          <div>
            <label class="hr-form-label">Alternate Contact Person</label>
            <input
              type="text"
              class="hr-input"
              v-model="formData.alternateContact"
              placeholder="Name and contact"
            >
          </div>
        </div>

        <div v-if="showWarning" class="hr-warning-box hr-mt-3">
          <i class="bx bx-error-circle hr-me-2"></i>{{ warningMessage }}
        </div>
      </form>
    </template>
    <template #footer>
      <button type="button" class="hr-btn hr-btn-secondary" @click="handleClose">Cancel</button>
      <button
        type="button"
        class="hr-btn hr-btn-primary"
        :disabled="loading || !isFormValid"
        @click="submitLeaveRequest"
      >
        <span v-if="loading" class="hr-spinner-inline"></span>
        {{ loading ? 'Submitting...' : 'Submit Application' }}
      </button>
    </template>
  </ModalWrapper>
</template>

<script>
/* eslint-disable */
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute } from "@/api";
import { fetchHolidayDatesInRange, countWorkingLeaveDays } from "@/utils/leaveWorkingDays";
import "@/assets/css/hr-page-shared.css";

export default {
  name: 'LeaveApplicationModal',
  components: { ModalWrapper },
  props: {
    employeeId: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      leaveTypes: [],
      leaveBalances: [],
      formData: {
        leaveTypeID: '',
        leaveTypeName: '',
        startDate: '',
        endDate: '',
        reason: '',
        contactNumber: '',
        alternateContact: '',
        documents: []
      },
      durationType: 'full',
      halfDaySession: 'morning',
      calculatedDays: 0,
      showWarning: false,
      warningMessage: '',
      minDate: '',
      selectedLeaveType: null,
      holidayDateSet: new Set(),
      holidaysLoadedKey: ''
    };
  },
  computed: {
    isFormValid() {
      return (
        this.formData.leaveTypeID &&
        this.formData.startDate &&
        this.formData.endDate &&
        (this.formData.reason || '').trim().length > 0 &&
        this.calculatedDays > 0 &&
        this.calculatedDays <= this.getAvailableDays()
      );
    }
  },
  watch: {
    durationType(newVal) {
      if (newVal === 'half') {
        this.formData.endDate = this.formData.startDate;
        this.calculatedDays = 0.5;
        this.holidaysLoadedKey = '';
      } else {
        this.holidaysLoadedKey = '';
        this.calculateDays();
      }
    },
    employeeId() {
      this.fetchLeaveBalances();
    }
  },
  mounted() {
    this.setMinDate();
    this.fetchLeaveTypes();
    this.fetchLeaveBalances();
  },
  methods: {
    open() {
      this.$refs.modal?.open();
    },
    close() {
      this.$refs.modal?.close();
    },
    handleClose() {
      this.resetForm();
      this.close();
    },
    setMinDate() {
      const today = new Date();
      today.setDate(today.getDate() + 1);
      this.minDate = today.toISOString().split('T')[0];
    },
    async fetchLeaveTypes() {
      const data = new FormData();
      data.append("function", "getActiveLeaveTypes");
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) this.leaveTypes = res.data.data.leaveTypes;
      } catch (error) {
        console.error('Error fetching leave types:', error);
      }
    },
    async fetchLeaveBalances() {
      const data = new FormData();
      data.append("function", "getEmployeeLeaveBalances");
      data.append("employeeId", this.employeeId);
      try {
        const res = await execute(data, authUrl);
        this.leaveBalances = res.data.data?.leaveBalances || [];
      } catch (error) {
        this.leaveBalances = [];
      }
    },
    onLeaveTypeChange() {
      this.selectedLeaveType = this.leaveTypes.find(t => t.id == this.formData.leaveTypeID);
      if (this.selectedLeaveType) this.formData.leaveTypeName = this.selectedLeaveType.typeName;
      this.checkLeaveBalance();
    },
    getAvailableDays() {
      if (!this.formData.leaveTypeID) return 0;
      const balance = this.leaveBalances.find(b => b.leaveTypeID == this.formData.leaveTypeID);
      return balance ? parseFloat(balance.remainingDays) : 0;
    },
    getAvailableDaysForType(leaveTypeId) {
      const balance = this.leaveBalances.find(b => b.leaveTypeID == leaveTypeId);
      return balance ? parseFloat(balance.remainingDays) : 0;
    },
    holidaysRangeKey() {
      if (!this.formData.startDate || !this.formData.endDate) return '';
      return `${this.formData.startDate}_${this.formData.endDate}`;
    },
    async refreshHolidaysIfNeeded() {
      const key = this.holidaysRangeKey();
      if (!key || key === this.holidaysLoadedKey) return;
      this.holidaysLoadedKey = key;
      this.holidayDateSet = await fetchHolidayDatesInRange(
        execute,
        authUrl,
        this.formData.startDate,
        this.formData.endDate
      );
    },
    async calculateDays() {
      if (!this.formData.startDate || !this.formData.endDate) {
        this.calculatedDays = 0;
        return;
      }
      if (this.durationType === 'half') {
        this.calculatedDays = 0.5;
        this.formData.endDate = this.formData.startDate;
        this.checkLeaveBalance();
        return;
      }
      const start = new Date(`${this.formData.startDate}T12:00:00`);
      const end = new Date(`${this.formData.endDate}T12:00:00`);
      if (end < start) {
        this.calculatedDays = 0;
        this.checkLeaveBalance();
        return;
      }
      await this.refreshHolidaysIfNeeded();
      this.calculatedDays = countWorkingLeaveDays(
        this.formData.startDate,
        this.formData.endDate,
        this.holidayDateSet
      );
      this.checkLeaveBalance();
    },
    checkLeaveBalance() {
      const available = this.getAvailableDays();
      if (this.calculatedDays > available) {
        this.showWarning = true;
        this.warningMessage = `You only have ${available} days available for this leave type.`;
      } else {
        this.showWarning = false;
        this.warningMessage = '';
      }
    },
    handleFileUpload(event) {
      const files = event.target.files;
      const maxSize = 5 * 1024 * 1024;
      for (let file of files) {
        if (file.size > maxSize) {
          alert(`File ${file.name} exceeds 5MB limit`);
          event.target.value = '';
          return;
        }
      }
      this.formData.documents = Array.from(files || []);
    },
    async submitLeaveRequest() {
      if (!this.isFormValid) return;
      this.loading = true;
      const data = new FormData();
      data.append("function", "submitLeaveRequest");
      data.append("leaveTypeID", this.formData.leaveTypeID);
      data.append("leaveTypeName", this.formData.leaveTypeName);
      data.append("startDate", this.formData.startDate);
      data.append("endDate", this.formData.endDate);
      data.append("reason", this.formData.reason);
      data.append("totalDays", this.calculatedDays);
      data.append("durationType", this.durationType);
      if (this.durationType === 'half') data.append("halfDaySession", this.halfDaySession);
      if (this.formData.contactNumber) data.append("contactNumber", this.formData.contactNumber);
      if (this.formData.alternateContact) data.append("alternateContact", this.formData.alternateContact);
      if (this.formData.documents.length > 0) {
        data.append("document", this.formData.documents[0]);
      }
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.$emit('leave-submitted', res.data.data?.leaveRequest || null);
          this.close();
          this.resetForm();
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        this.$emit('error', error.message || 'Failed to submit leave request');
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.formData = {
        leaveTypeID: '',
        leaveTypeName: '',
        startDate: '',
        endDate: '',
        reason: '',
        contactNumber: '',
        alternateContact: '',
        documents: []
      };
      this.durationType = 'full';
      this.halfDaySession = 'morning';
      this.calculatedDays = 0;
      this.holidaysLoadedKey = '';
      this.holidayDateSet = new Set();
      this.showWarning = false;
      this.warningMessage = '';
      this.selectedLeaveType = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    }
  }
};
</script>

<style scoped>
.hr-radio-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #475569;
}

.hr-radio-wrap input {
  width: 18px;
  height: 18px;
  accent-color: #059669;
}

.text-exceed { color: #ef4444; margin-left: 8px; }

.hr-warning-box {
  padding: 12px 16px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  color: #b45309;
  font-size: 14px;
}

.hr-spinner-inline {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #059669;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.hr-info-block .hr-info-block-body {
  display: flex;
  align-items: center;
}
</style>
